import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createWholesaleDeal, deleteWholesaleDeal, getDealForProperty, getMyWholesaleDeals, getWholesalerProfile,
  saveWholesalerProfile, updateWholesaleDeal,
} from '../../../api/Wholesale/wholesale';
import { WholesaleDeal, WholesalerProfile } from '../../../utility/wholesale';

const PROFILE_KEY = [ 'wholesalerProfile' ];
const DEALS_KEY = [ 'myWholesaleDeals' ];

export function useWholesalerProfile() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: PROFILE_KEY, queryFn: () => getWholesalerProfile() });
  const save = useMutation({
    mutationFn: (values: Partial<WholesalerProfile>) => saveWholesalerProfile(values),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROFILE_KEY }),
  });
  return {
    profile: query.data?.data.results as WholesalerProfile | undefined,
    canPost: Boolean(query.data?.data.can_post),
    isLoading: query.isLoading,
    save,
  };
}

export function useMyWholesaleDeals() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: DEALS_KEY, queryFn: () => getMyWholesaleDeals() });
  const onSuccess = () => queryClient.invalidateQueries({ queryKey: DEALS_KEY });
  return {
    deals: (query.data?.data.results as WholesaleDeal[]) || [],
    isLoading: query.isLoading,
    create: useMutation({ mutationFn: (body: FormData) => createWholesaleDeal(body), onSuccess }),
    update: useMutation({ mutationFn: ({ id, body }: { id: string; body: FormData }) => updateWholesaleDeal(id, body), onSuccess }),
    remove: useMutation({ mutationFn: (id: string) => deleteWholesaleDeal(id), onSuccess }),
  };
}

// The live wholesale deal for the property being viewed (Pro members only).
export function useDealForProperty(propertyId: string | undefined, enabled = true) {
  const query = useQuery({
    queryKey: [ 'wholesaleDealForProperty', propertyId ],
    queryFn: () => getDealForProperty(propertyId as string),
    enabled: enabled && !!propertyId,
    retry: false,
  });
  return { deal: (query.data?.data.result as WholesaleDeal | null) ?? null, isLoading: query.isLoading };
}
